/**
 * 이용권 관리 — 마스터-디테일
 *
 *  매장 관리(/business) 와 동일한 패턴.
 *  좌측: 상태 탭 + 검색/필터 + 발급된 이용권 목록
 *  우측: 선택한 이용권 상세 (요약 / 사용이력 / 환원이력 탭 + 결제취소)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Ticket } from 'lucide-react';
import {
  ticketsApi,
  type TicketSummary,
  type TicketPaymentType,
  type TicketProductType,
} from '@/services/api';
import { StatusBadge, type BadgeTone } from '@/components/common';
import { TicketDetailPanel } from './TicketDetailPanel';
import { TicketRefundModal } from './TicketRefundModal';

const PRODUCT_TYPE_LABEL: Record<TicketProductType, string> = {
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

type TicketStatus = TicketSummary['ticketStatus'];

const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  ACTIVE: '유효',
  EXHAUSTED: '소진',
  EXPIRED: '만료',
  REVOKED: '회수',
};

const TICKET_STATUS_TONE: Record<TicketStatus, BadgeTone> = {
  ACTIVE: 'success',
  EXHAUSTED: 'neutral',
  EXPIRED: 'danger',
  REVOKED: 'warning',
};

const PAYMENT_TYPE_LABEL: Record<TicketPaymentType, string> = {
  ONLINE: '온라인',
  OFFLINE: '수동발급',
};

type StatusTab = 'ALL' | TicketStatus;

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'ACTIVE', label: '유효' },
  { key: 'EXHAUSTED', label: '소진' },
  { key: 'EXPIRED', label: '만료' },
  { key: 'REVOKED', label: '회수' },
];

/** 오늘 날짜 YYYY-MM-DD (KST) */
function todayStr(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 잔여 표기 — 호텔 이용권은 박수 누적 차감이 없고 1회 사용 시 전량 소진되므로
 * 잔여 박수를 보여주지 않는다.
 */
function formatRemaining(t: TicketSummary): string {
  // 회수된 이용권은 잔여가 없다 (사용 이력과 무관하게 사용 불가)
  if (t.ticketStatus === 'REVOKED') {
    return '회수됨';
  }
  if (t.productType === 'PRODUCT_TYPE_HOTEL') {
    return t.usedQty > 0 ? '사용 완료' : `${t.splitCount}박 (미사용)`;
  }
  return `잔여 ${t.remainingQty} / ${t.splitCount}회`;
}

export function TicketListPage() {
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [tab, setTab] = useState<StatusTab>('ALL');
  const [productType, setProductType] = useState<'ALL' | TicketProductType>('ALL');
  const [paymentType, setPaymentType] = useState<'ALL' | TicketPaymentType>('ALL');
  // 발급일자 단일 일자 (YYYY-MM-DD). 기본값은 오늘, 비우면 전체 기간.
  const [dtRegist, setDtRegist] = useState<string>(() => todayStr());

  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [refundSeq, setRefundSeq] = useState<number | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setAppliedKeyword(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [
      'tickets',
      { keyword: appliedKeyword, productType, paymentType, dtRegist },
    ],
    queryFn: () =>
      ticketsApi.getTickets({
        keyword: appliedKeyword || undefined,
        // 상태는 좌측 탭에서 나누므로 서버에서는 전체를 받는다 (탭별 건수 표시용)
        status: 'ALL',
        productType,
        paymentType,
        dtRegist: dtRegist || undefined,
      }),
    staleTime: 30 * 1000,
  });

  const allRows = useMemo(() => data ?? [], [data]);

  const stats = useMemo(() => {
    let active = 0;
    let exhausted = 0;
    let expired = 0;
    let revoked = 0;
    let totalAmount = 0;
    for (const r of allRows) {
      if (r.ticketStatus === 'ACTIVE') active++;
      else if (r.ticketStatus === 'EXHAUSTED') exhausted++;
      else if (r.ticketStatus === 'REVOKED') revoked++;
      else expired++;
      // 온라인 결제는 COMPLETED, 수동발급은 PAID. 회수/환불분은 결제액에서 제외.
      if (r.ticketStatus !== 'REVOKED' && (r.paymentStatus === 'PAID' || r.paymentStatus === 'COMPLETED')) {
        totalAmount += r.amount;
      }
    }
    return { total: allRows.length, active, exhausted, expired, revoked, totalAmount };
  }, [allRows]);

  const rows = useMemo(
    () => (tab === 'ALL' ? allRows : allRows.filter(r => r.ticketStatus === tab)),
    [allRows, tab],
  );

  const tabCounts: Record<StatusTab, number> = {
    ALL: stats.total,
    ACTIVE: stats.active,
    EXHAUSTED: stats.exhausted,
    EXPIRED: stats.expired,
    REVOKED: stats.revoked,
  };

  // 조회 조건/탭이 바뀌면 목록 스크롤을 위로
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [appliedKeyword, productType, paymentType, dtRegist, tab]);

  // 선택한 이용권이 목록에서 사라지면 선택 해제
  useEffect(() => {
    if (selectedSeq !== null && !allRows.some(r => r.purchaseTicketSeq === selectedSeq)) {
      setSelectedSeq(null);
    }
  }, [allRows, selectedSeq]);

  const selectCls =
    'rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main 패딩 고려해 calc
    <div className="flex h-[calc(100vh-120px)] flex-col">
      <header className="mb-3 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Ticket className="text-gray-500" size={22} /> 이용권 관리
          </h1>
          <p className="mt-0.5 text-xs text-gray-500">
            좌측에서 이용권을 선택하면 사용/환원 이력이 우측에 표시됩니다.
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5 text-xs">
          <span className="rounded-md bg-gray-100 px-2.5 py-1.5 text-gray-600">
            조회 <strong className="tabular-nums">{stats.total}</strong>
          </span>
          <span className="rounded-md bg-green-50 px-2.5 py-1.5 text-green-700">
            유효 <strong className="tabular-nums">{stats.active}</strong>
          </span>
          <span className="rounded-md bg-gray-100 px-2.5 py-1.5 text-gray-600">
            소진/만료{' '}
            <strong className="tabular-nums">{stats.exhausted + stats.expired}</strong>
          </span>
          <span className="rounded-md bg-amber-50 px-2.5 py-1.5 text-amber-700">
            회수 <strong className="tabular-nums">{stats.revoked}</strong>
          </span>
          <span className="rounded-md bg-primary-50 px-2.5 py-1.5 text-primary-700">
            결제액{' '}
            <strong className="tabular-nums">{stats.totalAmount.toLocaleString()}원</strong>
          </span>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        {/* ── 좌측: 이용권 목록 (5/12) ── */}
        <div className="col-span-12 flex min-h-0 flex-col lg:col-span-5">
          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="회원 / 펫 / 사업장 / 이용권명 / 주문번호"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* 필터 */}
          <div className="mb-2 flex gap-2">
            <select
              value={productType}
              onChange={e => setProductType(e.target.value as 'ALL' | TicketProductType)}
              className={`flex-1 ${selectCls}`}
            >
              <option value="ALL">상품유형 전체</option>
              <option value="PRODUCT_TYPE_KINDERGARTEN">유치원</option>
              <option value="PRODUCT_TYPE_DAYCARE">데이케어</option>
              <option value="PRODUCT_TYPE_HOTEL">호텔</option>
              <option value="PRODUCT_TYPE_CLASS">수업</option>
              <option value="PRODUCT_TYPE_GROOMING">미용</option>
            </select>
            <select
              value={paymentType}
              onChange={e => setPaymentType(e.target.value as 'ALL' | TicketPaymentType)}
              className={`flex-1 ${selectCls}`}
            >
              <option value="ALL">발급경로 전체</option>
              <option value="ONLINE">온라인</option>
              <option value="OFFLINE">오프라인(수동)</option>
            </select>
          </div>

          {/* 발급일자 — 비우면 전체 기간 */}
          <div className="mb-2 flex items-center gap-2">
            <span className="shrink-0 text-xs text-gray-500">발급일</span>
            <input
              type="date"
              value={dtRegist}
              onChange={e => setDtRegist(e.target.value)}
              className={`flex-1 ${selectCls}`}
            />
            <button
              type="button"
              onClick={() => setDtRegist(dtRegist ? '' : todayStr())}
              className={`shrink-0 rounded-md border px-2 py-2 text-xs transition-colors ${
                dtRegist
                  ? 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  : 'border-primary-500 bg-primary-50 text-primary-700'
              }`}
            >
              {dtRegist ? '전체 기간' : '오늘'}
            </button>
          </div>

          {/* 상태 탭 */}
          <div className="mb-2 flex shrink-0 gap-1 border-b border-gray-200">
            {STATUS_TABS.map(t => {
              const active = tab === t.key;
              const count = tabCounts[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`-mb-px shrink-0 border-b-2 px-2.5 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {t.label}
                  {count > 0 && (
                    <span
                      className={`ml-1 text-xs tabular-nums ${
                        active ? 'text-primary-500' : 'text-gray-400'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              {rows.length.toLocaleString()}건 표시
              {dtRegist ? ` · ${dtRegist} 발급분` : ' · 전체 기간'}
            </span>
            {isFetching && !isLoading && <span className="text-gray-400">불러오는 중…</span>}
          </div>

          {/* 리스트 — 내부 스크롤 */}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div ref={listRef} className="h-full divide-y divide-gray-100 overflow-y-auto">
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!isLoading && error && (
                <div className="px-4 py-8 text-center text-sm text-red-500">
                  이용권 정보를 불러오지 못했습니다.
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="ml-2 underline hover:text-red-700"
                  >
                    다시 시도
                  </button>
                </div>
              )}
              {!isLoading && !error && rows.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  조회된 이용권이 없습니다.
                </div>
              )}
              {!isLoading &&
                !error &&
                rows.map(row => {
                  const selected = selectedSeq === row.purchaseTicketSeq;
                  const expiringSoon =
                    row.ticketStatus === 'ACTIVE' &&
                    row.daysToExpire >= 0 &&
                    row.daysToExpire <= 14;
                  const meta = [
                    `${row.userName}${row.petName ? ` / ${row.petName}` : ''}`,
                    row.companyName,
                    `${Number(row.amount ?? 0).toLocaleString()}원`,
                    PAYMENT_TYPE_LABEL[row.paymentType] ?? row.paymentType,
                    `${(row.dtRegist ?? '').slice(0, 10)} 발급`,
                  ]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <div
                      key={row.purchaseTicketSeq}
                      onClick={() => setSelectedSeq(row.purchaseTicketSeq)}
                      // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                      // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                      className={`cursor-pointer px-3 py-2.5 transition-colors ${
                        selected
                          ? 'border-l-4 !border-l-primary-500 bg-primary-50 pl-2'
                          : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <span className="truncate text-sm font-medium text-gray-900">
                            {row.name}
                          </span>
                          <StatusBadge tone={TICKET_STATUS_TONE[row.ticketStatus]}>
                            {TICKET_STATUS_LABEL[row.ticketStatus]}
                          </StatusBadge>
                          <span className="shrink-0 text-[11px] text-gray-500">
                            {PRODUCT_TYPE_LABEL[row.productType] ?? row.productType}
                          </span>
                        </div>
                        <div className="shrink-0 text-right">
                          <span
                            className={`text-[11px] ${
                              row.ticketStatus === 'ACTIVE'
                                ? 'font-semibold text-primary-600'
                                : 'text-gray-500'
                            }`}
                          >
                            {formatRemaining(row)}
                          </span>
                          {expiringSoon && (
                            <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-700">
                              D-{row.daysToExpire}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
                        {meta}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* ── 우측: 이용권 상세 (7/12) ── */}
        <div className="col-span-12 min-h-0 lg:col-span-7">
          {selectedSeq === null ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Ticket className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">
                  좌측에서 이용권을 선택하면
                  <br />
                  상세 정보가 표시됩니다
                </div>
              </div>
            </div>
          ) : (
            <TicketDetailPanel
              key={selectedSeq}
              purchaseTicketSeq={selectedSeq}
              onClose={() => setSelectedSeq(null)}
              onRefund={setRefundSeq}
            />
          )}
        </div>
      </div>

      <TicketRefundModal
        purchaseTicketSeq={refundSeq}
        onClose={() => setRefundSeq(null)}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
